/**
 * Endpoint modules.
 *
 * Every network call the application makes goes through a named function here, so no component
 * contains a URL string. That keeps API changes to one file and makes the surface testable.
 */
import { apiDelete, apiGet, apiPatch, apiPost, apiPostForm, type Paginated, type QueryParams } from './client';
import type {
  AboutBundle,
  AnalyticsConfig,
  ApplicationLimits,
  ApplicationRecord,
  ArticleCard,
  ArticleDetail,
  ArticleFacets,
  AuditEntry,
  CaseStudyCard,
  CaseStudyDetail,
  CaseStudyFacets,
  CurrentUser,
  DashboardPayload,
  DocumentRef,
  Faq,
  HomePayload,
  HomeSection,
  IndustryCard,
  IndustryDetail,
  JobOpeningCard,
  JobOpeningDetail,
  LeadDetailRecord,
  LeadRecord,
  LeadSubmission,
  LoginResponse,
  MediaAsset,
  Menu,
  PageDetail,
  ProductCard,
  ProductDetail,
  ResolvedMeta,
  Revision,
  RoleSummary,
  SearchResponse,
  ServiceCard,
  ServiceDetail,
  SiteSettings,
  SolutionCard,
  SolutionDetail,
  SubmissionResult,
  TechnologyPage,
  UserRecord,
} from './types';

/* ------------------------------------------------------------------ site */

export const siteApi = {
  settings: () => apiGet<SiteSettings>('/settings/'),
  menus: () => apiGet<Menu[]>('/menus/all/'),
  home: () => apiGet<HomePayload>('/home/'),
  about: () => apiGet<AboutBundle>('/about/'),
  faqs: (params?: QueryParams) => apiGet<Faq[]>('/faqs/', params),
  search: (query: string, limit = 6) => apiGet<SearchResponse>('/search/', { q: query, limit }),
  resolveMeta: (path: string) => apiGet<ResolvedMeta>('/seo/resolve/', { path }),
  analyticsConfig: () => apiGet<AnalyticsConfig>('/analytics/config/'),
  pageByPath: (path: string) => apiGet<PageDetail>('/pages/by-path/', { path }),
  pageTree: () => apiGet<{ id: number; title: string; path: string }[]>('/pages/tree/'),
};

/* ------------------------------------------------------------------ catalogue */

export const catalogApi = {
  solutions: (params?: QueryParams) => apiGet<Paginated<SolutionCard>>('/solutions/', params),
  solution: (slug: string, params?: QueryParams) => apiGet<SolutionDetail>(`/solutions/${slug}/`, params),
  products: (params?: QueryParams) => apiGet<Paginated<ProductCard>>('/products/', params),
  product: (slug: string, params?: QueryParams) => apiGet<ProductDetail>(`/products/${slug}/`, params),
  services: (params?: QueryParams) => apiGet<Paginated<ServiceCard>>('/services/', params),
  service: (slug: string, params?: QueryParams) => apiGet<ServiceDetail>(`/services/${slug}/`, params),
  industries: (params?: QueryParams) => apiGet<Paginated<IndustryCard>>('/industries/', params),
  industry: (slug: string, params?: QueryParams) => apiGet<IndustryDetail>(`/industries/${slug}/`, params),
  technologyPage: () => apiGet<TechnologyPage>('/technology-capabilities/page/'),
};

/* ------------------------------------------------------------------ case studies & content */

export const caseStudyApi = {
  list: (params?: QueryParams) => apiGet<Paginated<CaseStudyCard>>('/case-studies/', params),
  detail: (slug: string, params?: QueryParams) => apiGet<CaseStudyDetail>(`/case-studies/${slug}/`, params),
  facets: () => apiGet<CaseStudyFacets>('/case-studies/facets/'),
};

export const contentApi = {
  articles: (params?: QueryParams) => apiGet<Paginated<ArticleCard>>('/articles/', params),
  article: (slug: string, params?: QueryParams) => apiGet<ArticleDetail>(`/articles/${slug}/`, params),
  facets: () => apiGet<ArticleFacets>('/articles/facets/'),
  latest: (limit = 3) => apiGet<ArticleCard[]>('/articles/latest/', { limit }),
  documents: (params?: QueryParams) => apiGet<Paginated<DocumentRef>>('/media/documents/', params),
};

/* ------------------------------------------------------------------ careers */

export const careersApi = {
  openings: (params?: QueryParams) => apiGet<Paginated<JobOpeningCard>>('/careers/openings/', params),
  opening: (slug: string, params?: QueryParams) => apiGet<JobOpeningDetail>(`/careers/openings/${slug}/`, params),
  departments: () => apiGet<{ id: number; name: string; slug: string; open_count: number }[]>('/careers/departments/'),
  applicationLimits: () => apiGet<ApplicationLimits>('/careers/openings/application-limits/'),
  apply: (form: FormData) => apiPostForm<SubmissionResult>('/careers/applications/', form),
};

/* ------------------------------------------------------------------ leads */

export const leadApi = {
  submit: (payload: LeadSubmission) => apiPost<SubmissionResult>('/leads/', payload),
  subscribeNewsletter: (payload: { email: string; full_name?: string; source_page?: string; honeypot?: string }) =>
    apiPost<{ email: string; created: boolean; message: string }>('/newsletter/', payload),
};

/* ------------------------------------------------------------------ auth */

export const authApi = {
  login: (email: string, password: string) => apiPost<LoginResponse>('/auth/login/', { email, password }),
  refresh: () => apiPost<{ access: string }>('/auth/refresh/', {}),
  logout: () => apiPost<void>('/auth/logout/', {}),
  me: () => apiGet<CurrentUser>('/auth/me/'),
  updateProfile: (payload: Partial<Pick<CurrentUser, 'full_name' | 'job_title' | 'phone' | 'bio'>>) =>
    apiPatch<CurrentUser>('/auth/me/', payload),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiPost<void>('/auth/password/change/', { current_password: currentPassword, new_password: newPassword }),
  startTwoFactor: () => apiPost<{ provisioning_uri: string; secret: string }>('/auth/2fa/', {}),
  roles: () => apiGet<RoleSummary[]>('/auth/roles/'),
};

/* ------------------------------------------------------------------ CMS */

/** Content types the generic CMS list/detail screens operate on. */
export const CMS_RESOURCES = {
  pages: { path: 'pages', label: 'Pages', titleField: 'title' },
  products: { path: 'products', label: 'Products', titleField: 'name' },
  solutions: { path: 'solutions', label: 'Solutions', titleField: 'name' },
  services: { path: 'services', label: 'Services', titleField: 'name' },
  industries: { path: 'industries', label: 'Industries', titleField: 'name' },
  'case-studies': { path: 'case-studies', label: 'Case studies', titleField: 'title' },
  articles: { path: 'articles', label: 'Articles', titleField: 'title' },
} as const;

export type CmsResourceKey = keyof typeof CMS_RESOURCES;

export interface CmsRecord {
  id: number;
  slug: string;
  status: string;
  is_featured: boolean;
  order: number;
  needs_review?: boolean;
  updated_at: string;
  published_at: string | null;
  [key: string]: unknown;
}

export const cmsApi = {
  dashboard: () => apiGet<DashboardPayload>('/dashboard/'),
  myContent: () => apiGet<{ items: { type: string; id: number; title: string; status: string; updated_at: string; admin_path: string }[] }>(
    '/dashboard/my-content/',
  ),

  list: (resource: string, params?: QueryParams) => apiGet<Paginated<CmsRecord>>(`/${resource}/`, { cms: 1, ...params }),
  detail: (resource: string, slug: string) => apiGet<CmsRecord>(`/${resource}/${slug}/`, { cms: 1 }),
  create: (resource: string, payload: Record<string, unknown>) => apiPost<CmsRecord>(`/${resource}/?cms=1`, payload),
  update: (resource: string, slug: string, payload: Record<string, unknown>) =>
    apiPatch<CmsRecord>(`/${resource}/${slug}/?cms=1`, payload),
  remove: (resource: string, slug: string) => apiDelete(`/${resource}/${slug}/`),

  transition: (resource: string, slug: string, action: 'submit' | 'approve' | 'reject' | 'publish' | 'unpublish' | 'archive', comment = '') =>
    apiPost<CmsRecord>(`/${resource}/${slug}/${action}/`, { comment }),
  bulk: (resource: string, ids: number[], action: string) =>
    apiPost<{ updated: number[]; skipped: { id: number; detail: string }[] }>(`/${resource}/bulk/`, { ids, action }),

  revisions: (resource: string, slug: string) => apiGet<Paginated<Revision>>(`/${resource}/${slug}/revisions/`),
  restoreRevision: (resource: string, slug: string, version: number, comment = '') =>
    apiPost<CmsRecord>(`/${resource}/${slug}/revisions/${version}/restore/`, { comment }),
  previewToken: (resource: string, slug: string) => apiPost<{ token: string; url: string }>(`/${resource}/${slug}/preview-token/`, {}),

  leads: (params?: QueryParams) => apiGet<Paginated<LeadRecord>>('/leads/', params),
  lead: (id: number) => apiGet<LeadDetailRecord>(`/leads/${id}/`),
  leadPipeline: () => apiGet<{
    by_status: Record<string, number>;
    by_type: Record<string, number>;
    open_count: number;
    unassigned_count: number;
    last_30_days: { date: string; count: number }[];
  }>('/leads/pipeline/'),
  updateLeadStatus: (id: number, status: string, note = '') => apiPost<LeadDetailRecord>(`/leads/${id}/status/`, { status, note }),
  assignLead: (id: number, assignedTo: number | null) => apiPost<LeadDetailRecord>(`/leads/${id}/assign/`, { assigned_to: assignedTo }),
  addLeadNote: (id: number, body: string) => apiPost<{ id: number }>(`/leads/${id}/notes/`, { body }),

  applications: (params?: QueryParams) => apiGet<Paginated<ApplicationRecord>>('/careers/applications/', params),
  updateApplication: (id: number, payload: { status?: string; rating?: number; internal_notes?: string }) =>
    apiPatch<ApplicationRecord>(`/careers/applications/${id}/`, payload),

  media: (params?: QueryParams) => apiGet<Paginated<MediaAsset>>('/media/assets/', params),
  uploadMedia: (form: FormData) => apiPostForm<MediaAsset>('/media/assets/', form),
  updateMedia: (id: number, payload: Partial<Pick<MediaAsset, 'title' | 'alt_text' | 'caption' | 'credit' | 'tags'>>) =>
    apiPatch<MediaAsset>(`/media/assets/${id}/`, payload),
  deleteMedia: (id: number) => apiDelete(`/media/assets/${id}/`),
  mediaStats: () => apiGet<{ by_kind: Record<string, number>; total: number; total_bytes: number }>('/media/assets/stats/'),

  settings: () => apiGet<Record<string, unknown>>('/settings/', { cms: 1 }),
  updateSettings: (payload: Record<string, unknown>) => apiPatch<Record<string, unknown>>('/settings/', payload),

  menus: () => apiGet<Menu[]>('/menus/'),
  homeSections: () => apiGet<HomeSection[]>('/home-sections/'),
  updateHomeSection: (id: number, payload: Record<string, unknown>) => apiPatch<HomeSection>(`/home-sections/${id}/`, payload),
  reorderHomeSections: (order: number[]) => apiPost<{ updated: number[] }>('/home-sections/reorder/', { order }),

  auditLog: (params?: QueryParams) => apiGet<Paginated<AuditEntry>>('/audit-log/', params),

  users: (params?: QueryParams) => apiGet<Paginated<UserRecord>>('/users/', params),
  createUser: (payload: Record<string, unknown>) => apiPost<UserRecord>('/users/', payload),
  updateUser: (id: number, payload: Record<string, unknown>) => apiPatch<UserRecord>(`/users/${id}/`, payload),
  deactivateUser: (id: number) => apiDelete(`/users/${id}/`),
  activateUser: (id: number) => apiPost<UserRecord>(`/users/${id}/activate/`, {}),
  resetUserPassword: (id: number) => apiPost<{ temporary_password: string }>(`/users/${id}/reset-password/`, {}),
};
